import { useEffect, useRef, useState } from "react";
import { Lock, Globe, UserCircle2, Newspaper, ChevronDown, Check } from "lucide-react";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export const EMPTY_DESTS = { profile: false, feed: false, communities: [] };
const MAX_INLINE = 3;

export function summarize(d, comms) {
  const parts = [];
  if (d.profile) parts.push("Meu perfil");
  if (d.feed) parts.push("Feed geral");
  d.communities.forEach((id) => { const c = comms.find((x) => x.id === id); parts.push(c ? c.name : "Comunidade"); });
  return parts;
}

export function needsWiderConfirm(d, comms) {
  const sel = d.communities.map((id) => comms.find((x) => x.id === id)).filter(Boolean);
  const priv = sel.filter((c) => c.privacy === "private");
  const wider = d.profile || d.feed || sel.some((c) => c.privacy !== "private");
  return priv.length > 0 && wider ? priv : null;
}

function Option({ checked, onChange, icon: Icon, title, audience, testid, disabled }) {
  return (
    <label className={`flex items-start gap-2.5 px-3 py-2.5 rounded-lg ${disabled ? "opacity-60" : "cursor-pointer hover:bg-zinc-50 active:bg-zinc-100"}`} data-testid={testid}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-[#c8102e]"
             data-testid={`${testid}-input`} aria-describedby={`${testid}-audience`} />
      <span className="flex-1 min-w-0">
        <span className="flex items-center gap-1.5 text-sm font-semibold"><Icon size={14} className="shrink-0 text-zinc-500" aria-hidden="true" /> {title}{disabled && <span className="sr-only"> (comunidade atual, sempre incluída)</span>}</span>
        <span id={`${testid}-audience`} className="block text-[11px] text-zinc-500 leading-snug">{audience}</span>
      </span>
    </label>
  );
}

// Dropdown multi-seleção: fechado mostra «Onde publicar?» + resumo; aberto lista perfil, feed e comunidades permitidas (adesão pendente não conta).
export function DestinationPicker({ value, onChange, fixedCommunityId = null, onLoaded, onDisabled }) {
  const [info, setInfo] = useState(null);
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  useEffect(() => {
    api.get("/me/post-destinations").then((r) => {
      setInfo(r.data); onLoaded?.(r.data.communities);
      if (r.data.enabled === false) onDisabled?.();
    }).catch(() => setInfo({ profile: {}, feed: {}, communities: [] }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (info?.enabled === false) return null; // desligado: composer volta ao comportamento anterior, sem UI nova
  const comms = info?.communities || [];
  const toggleComm = (id, on) => onChange({ ...value, communities: on ? [...new Set([...value.communities, id])] : value.communities.filter((x) => x !== id) });
  const parts = summarize(value, comms);
  const count = parts.length;
  const short = count === 0 ? "Escolher destinos" : count > MAX_INLINE ? `${count} destinos selecionados` : parts.join(" · ");
  const done = () => { setOpen(false); triggerRef.current?.focus(); };

  return (
    <div className="mt-3" data-testid="destination-picker">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button ref={triggerRef} type="button" data-testid="dest-trigger" aria-haspopup="dialog" aria-expanded={open} aria-label={`Onde publicar? ${count ? parts.join(", ") : "nenhum destino escolhido"}`}
                  className="w-full flex items-center gap-2 rounded-xl border border-zinc-300 bg-white px-3 py-2.5 text-left hover:border-zinc-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-black/60 transition-colors">
            <span className="shrink-0 text-[11px] font-bold uppercase tracking-wide text-zinc-600">Onde publicar?</span>
            <span className={`flex-1 min-w-0 truncate text-sm ${count ? "font-semibold text-zinc-900" : "text-zinc-500"}`} data-testid="dest-trigger-summary" data-count={count}>{short}</span>
            <ChevronDown size={16} className={`shrink-0 text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" sideOffset={6} className="p-0 w-[var(--radix-popover-trigger-width)] max-w-[calc(100vw-1.5rem)] rounded-xl shadow-lg" data-testid="dest-popover"
                        onOpenAutoFocus={(e) => { e.preventDefault(); e.currentTarget.querySelector("input:not(:disabled)")?.focus(); }}>
          <div role="group" aria-label="Destinos da publicação" className="max-h-[min(50vh,320px)] overflow-y-auto overscroll-contain py-1" data-testid="dest-list">
            <Option checked={value.profile} onChange={(v) => onChange({ ...value, profile: v })} icon={UserCircle2} title="No meu perfil" testid="dest-profile"
                    audience={info?.profile?.audience || "Visível a todos os membros com sessão iniciada (não é privado)"} />
            <Option checked={value.feed} onChange={(v) => onChange({ ...value, feed: v })} icon={Newspaper} title="No feed geral" testid="dest-feed"
                    audience={info?.feed?.audience || "Visível a todos os membros no feed geral"} />
            <div className="px-3 pt-2 pb-1 text-[11px] font-bold uppercase tracking-wide text-zinc-500" data-testid="dest-communities-heading">Numa comunidade…</div>
            <div data-testid="dest-communities">
              {info && comms.length === 0 && <p className="text-[11px] text-zinc-500 px-3 pb-2">Ainda não participas em comunidades onde possas publicar.</p>}
              {comms.map((c) => (
                <Option key={c.id} checked={value.communities.includes(c.id)} onChange={(v) => toggleComm(c.id, v)} icon={c.privacy === "private" ? Lock : Globe} title={c.name} testid={`dest-comm-${c.id}`}
                        disabled={fixedCommunityId === c.id}
                        audience={`${c.privacy === "private" ? "Comunidade privada — só participantes aprovadas" : "Comunidade pública — visível a todos os membros"}${c.requires_approval ? " · sujeita a aprovação" : ""}`} />
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between gap-2 border-t border-zinc-200 px-3 py-2 bg-white rounded-b-xl">
            <span className="text-[11px] text-zinc-500" aria-live="polite" data-testid="dest-popover-count">{count ? `${count} selecionado${count > 1 ? "s" : ""}` : "Nenhum destino"}</span>
            <button type="button" onClick={done} data-testid="dest-done" className="min-h-[36px] px-4 rounded-xl bg-black text-white text-xs font-semibold inline-flex items-center gap-1"><Check size={13} aria-hidden="true" /> Concluir</button>
          </div>
        </PopoverContent>
      </Popover>
      <div className="mt-1.5 text-xs" data-testid="dest-summary" data-count={count} aria-live="polite">
        {count ? <><span className="text-zinc-500">Publicar em:</span> <span className="font-semibold break-words">{parts.join(" · ")}</span></> : <span className="text-[#c8102e] font-semibold">Escolhe pelo menos um destino</span>}
      </div>
    </div>
  );
}
